/**
 * Home health demo — clock and date-key helpers.
 *
 * The demo never time-travels: "now" is always the wall clock, and the seed is
 * built for the calendar date it runs on. Beats that need a later stage are
 * shown on a different patient who is already at that stage.
 *
 * All date keys ("YYYY-MM-DD") are in the branch time zone, never the browser's.
 * Day arithmetic on keys is done in UTC so it is independent of the runtime zone
 * and of DST in either zone.
 */
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import type { DateKey, DemoState } from "../state";

const DAY_MS = 86_400_000;

export function toDateKey(date: Date, timeZone: string): DateKey {
  return formatInTimeZone(date, timeZone, "yyyy-MM-dd");
}

export function dateKeyStart(key: DateKey, timeZone: string): Date {
  return fromZonedTime(`${key}T00:00:00`, timeZone);
}

/** Instant for `HH:mm` wall time on the given day in the branch zone. */
export function dateKeyAt(key: DateKey, hhmm: string, timeZone: string): Date {
  return fromZonedTime(`${key}T${hhmm}:00`, timeZone);
}

function keyToUtc(key: DateKey): Date {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

function utcToKey(date: Date): DateKey {
  return date.toISOString().slice(0, 10);
}

export function addDaysToKey(key: DateKey, days: number): DateKey {
  const base = keyToUtc(key);
  return utcToKey(new Date(base.getTime() + days * DAY_MS));
}

export function daysBetweenKeys(from: DateKey, to: DateKey): number {
  return Math.round((keyToUtc(to).getTime() - keyToUtc(from).getTime()) / DAY_MS);
}

/** ISO weekday 1 = Monday … 7 = Sunday. */
export function isoWeekday(key: DateKey): number {
  const day = keyToUtc(key).getUTCDay();
  return day === 0 ? 7 : day;
}

export function weekStartKey(key: DateKey): DateKey {
  return addDaysToKey(key, 1 - isoWeekday(key));
}

export function isWeekendKey(key: DateKey): boolean {
  return isoWeekday(key) >= 6;
}

/** A branch working day: Monday to Friday. The branch's weekend is covered by per diems and on-call, never by the acts. */
export function isWorkingDayKey(key: DateKey): boolean {
  return isoWeekday(key) <= 5;
}

/**
 * Working days, not calendar days. The demo's anchors are "today", "the next
 * working day" and "the one after that", so a Friday rehearsal's round is about
 * Monday and a Thursday rehearsal's about Friday — no beat ever lands on a
 * Saturday. `n = 0` returns the key unchanged, even on a weekend.
 */
/**
 * **A start of care some days before demo day, always on a working day.** A
 * recent admission (under a week) counts working days, so "admitted yesterday"
 * on a Monday is Friday and matches the visits the script pins by working day;
 * an older one counts calendar days and, landing on a weekend, moves on to the
 * Monday — forward, so a window the cast counts toward (a recertification due
 * in a few days) never slips into the past. A Saturday start of care put its
 * first plan week across the weekend and its first visits before the episode
 * began.
 */
export function socDateBefore(today: DateKey, daysAgo: number): DateKey {
  if (daysAgo < 7) return addWorkingDaysToKey(today, -daysAgo);
  let out = addDaysToKey(today, -daysAgo);
  while (!isWorkingDayKey(out)) out = addDaysToKey(out, 1);
  return out;
}

export function addWorkingDaysToKey(key: DateKey, n: number): DateKey {
  const step = n >= 0 ? 1 : -1;
  let out = key;
  for (let i = 0; i < Math.abs(n); i++) {
    do {
      out = addDaysToKey(out, step);
    } while (!isWorkingDayKey(out));
  }
  return out;
}

/** The next working day strictly after `key` — the day "tomorrow" means on every screen. */
export function nextWorkingDayKey(key: DateKey): DateKey {
  return addWorkingDaysToKey(key, 1);
}

export function dateKeyRange(from: DateKey, toInclusive: DateKey): DateKey[] {
  const n = daysBetweenKeys(from, toInclusive);
  if (n < 0) return [];
  return Array.from({ length: n + 1 }, (_, i) => addDaysToKey(from, i));
}

/** True when the seed was built for a different calendar date than today — re-seed before rehearsing. */
export function isSeedStale(state: Pick<DemoState, "meta" | "config">, wallNow: Date = new Date()): boolean {
  return toDateKey(wallNow, state.config.branch.timeZone) !== state.meta.demoToday;
}

/**
 * **The store's demo day against today.** A seed built for tomorrow is not
 * stale — it is Thursday evening and the session is Friday, which is why the
 * tenant carries its own demo day — so the drawer says "tomorrow", not "stale".
 * A store built for a different day from its tenant's cannot happen any more:
 * the horizon rebuilds the store whenever the tenant's seed stamp changes.
 */
export function seedDayStanding(state: Pick<DemoState, "meta" | "config">, wallNow: Date = new Date()): { day: DateKey; relative: string } {
  const today = toDateKey(wallNow, state.config.branch.timeZone);
  const day = state.meta.demoToday;
  const days = daysBetweenKeys(today, day);
  const relative = days === 0 ? "today" : days === 1 ? "tomorrow" : days === -1 ? "yesterday" : days > 0 ? `in ${days} days` : `${-days} days ago`;
  return { day, relative };
}

/**
 * **The demo's "now": the seed's day, at the wall clock's hour.**
 *
 * Two facts get confused here and the confusion has cost us a screen at a time.
 * The *hour* is real — the room is in the room at the real time, and a timeline's
 * red line, a 10:00 cutoff and "has this visit started" all want the true clock.
 * The *day* belongs to the seed, because the tenant, the store and every fixture
 * were built for one calendar date. Rehearsing on Thursday evening for a Friday
 * session, the browser says Thursday and every screen that asked it answered a
 * question the data could not: the confirmation round went out for Monday, My
 * week marked the wrong column today, the pending list expired a hold early.
 *
 * So: the demo day, carrying the wall clock's time of day in the branch zone.
 *
 * On the day itself the two agree and this returns `wallNow` unchanged, byte for
 * byte — the early return is the point. Nothing this touches behaves differently
 * in the room than it did before.
 */
export function selectDemoNow(state: Pick<DemoState, "meta" | "config">, wallNow: Date = new Date()): Date {
  const timeZone = state.config.branch.timeZone;
  const day = state.meta?.demoToday;
  if (!day || day === toDateKey(wallNow, timeZone)) return wallNow;
  return fromZonedTime(`${day}T${formatInTimeZone(wallNow, timeZone, "HH:mm:ss.SSS")}`, timeZone);
}

/**
 * A **date** in the branch's zone. The pattern is required: it used to default to
 * `"EEE MMM d · HH:mm"`, which quietly put a hardcoded twenty-four-hour clock on
 * screen wherever a caller left it off. Clock times go through `useBranchClock`
 * now, which reads the user's own setting; this is for the day, the weekday and
 * the month, none of which the setting touches.
 */
export function formatInBranchZone(date: Date, timeZone: string, pattern: string): string {
  return formatInTimeZone(date, timeZone, pattern);
}

/** Short weekday names in ISO order, Monday first. */
export const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** "Mon,Tue,Wed" or "Mon-Fri" → ISO weekdays. */
export function parseDays(value: string): number[] {
  const out = new Set<number>();
  for (const part of value.split(",").map((p) => p.trim()).filter(Boolean)) {
    const range = part.split("-").map((p) => p.trim());
    const a = DAY_NAMES.indexOf(range[0]);
    const b = range.length > 1 ? DAY_NAMES.indexOf(range[1]) : a;
    if (a < 0 || b < 0) continue;
    for (let i = a; i <= b; i++) out.add(i + 1);
  }
  return [...out].sort();
}
